<template>
  <div class="grid">
    <div
        v-if="loading"
        class="flex col-12 justify-content-center"
        style="position: absolute; top: 50%; transform: translate(0, -50%);"
    >
      <ProgressSpinner />
    </div>

    <div v-else class="col-12 lg:col-4">
      <Card>
        <template #title>Поиск лимитов</template>
        <template #content>
          <AbstractForm
              ref="form"
              v-model="searchForm"
              :loading="formLoading"
              :on-commit="searchLimits"
              :after-clear="handleAfterClear"
              commit-label="Найти"
              commit-icon="pi pi-search"
          />
        </template>
      </Card>
    </div>

    <div v-if="limits.length > 0 && !loading" class="col-12 lg:col-8">
      <Card>
        <template #title>Найденные лимиты ({{ limits.length }})</template>
        <template #content>
          <div v-if="limits.length > 0" class="mb-4 p-3 surface-ground border-round">
            <div class="flex align-items-center justify-content-between">
              <span class="font-bold">Действия для всех лимитов:</span>
              <div class="flex align-items-center gap-3">
                <div
                    v-for="bureau in bureaus"
                    :key="bureau.value"
                    class="flex align-items-center gap-2"
                >
                  <Checkbox
                      v-model="globalSelectedBureaus"
                      :inputId="`global-${bureau.value}`"
                      :value="bureau.value"
                  />
                  <label :for="`global-${bureau.value}`" class="text-sm">
                    {{ bureau.label }}
                  </label>
                </div>

                <Button
                    label="Отправить закрытые"
                    icon="pi pi-send"
                    :disabled="globalSelectedBureaus.length === 0 || !hasClosedLimits"
                    @click="sendClosedLimits"
                    severity="success"
                />
              </div>
            </div>
            <div v-if="!hasClosedLimits" class="text-sm text-warning mt-2">
              <i class="pi pi-exclamation-triangle mr-1"></i>
              Нет закрытых лимитов для отправки
            </div>
          </div>

          <Accordion :multiple="true" :activeIndex="[0]">
            <AccordionTab
                v-for="(limit, index) in limits"
                :key="limit.id || limit.cbUuid"
                :header="getLimitHeader(limit, index)"
            >
              <Limit :limit="limit" />

              <template #footer>
                <Message
                    v-if="limit.status === 'closed'"
                    severity="success"
                    :closable="false"
                    class="mt-3"
                >
                  Лимит закрыт
                </Message>
                <Message
                    v-else
                    severity="warn"
                    :closable="false"
                    class="mt-3"
                >
                  Лимит активен - нельзя отправить на закрытие
                </Message>

                <div class="flex flex-wrap align-items-center gap-4 mt-3">
                  <div class="flex align-items-center gap-3">
                    <div
                        v-for="bureau in bureaus"
                        :key="bureau.value"
                        class="flex align-items-center gap-2"
                    >
                      <Checkbox
                          v-model="selectedBureaus[limit.id || limit.cbUuid]"
                          :inputId="`${limit.id || limit.cbUuid}-${bureau.value}`"
                          :value="bureau.value"
                      />
                      <label :for="`${limit.id || limit.cbUuid}-${bureau.value}`" class="text-sm">
                        {{ bureau.label }}
                      </label>
                    </div>
                  </div>

                  <SplitButton
                      :label="limit.status === 'closed' ? 'Отправить' : 'Недоступно'"
                      :icon="limit.status === 'closed' ? 'pi pi-send' : 'pi pi-ban'"
                      :model="getActionItemsForLimit(limit)"
                      :disabled="!hasSelectedBureausForLimit(limit) || limit.status !== 'closed'"
                      :severity="limit.status === 'closed' ? 'success' : 'secondary'"
                  />
                </div>
              </template>
            </AccordionTab>
          </Accordion>
        </template>
      </Card>
    </div>

    <Dialog
        v-model:visible="showSendDialog"
        :header="sendDialogHeader"
        :modal="true"
        :closable="false"
        :style="{ width: '450px' }"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem; color: #f59e0b" />
        <span v-if="limitsToSend.length === 1">
          Отправить информацию о закрытом лимите?
        </span>
        <span v-else>
          Отправить информацию о {{ limitsToSend.length }} закрытых лимитах?
        </span>

        <div class="mt-3 p-3 surface-ground border-round">
          <div><strong>Выбранные бюро:</strong></div>
          <div>{{ getSelectedBureausLabels() }}</div>
        </div>
      </div>

      <template #footer>
        <Button
            label="Нет"
            icon="pi pi-times"
            @click="showSendDialog = false"
            class="p-button-text"
        />
        <Button
            label="Да, отправить"
            icon="pi pi-send"
            @click="confirmSendClosedLimits"
            class="p-button-success"
            :loading="sendingInProgress"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import {defineComponent} from 'vue'
import AbstractForm from "@/components/AbstractForm.vue"
import FormModel from "@/model/FormModel"
import ResponseService from "@/service/ResponseService"
import LimitService from "@/service/LimitService"
import Checkbox from 'primevue/checkbox'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Limit from "@/components/Event/Limit.vue"
import {showErrorToast, showToast} from "@/helpers/actions/helpers";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-[0-9a-f]+$/i
const ID_REGEX = /^\d+$/

const BUREAUS = [
  { label: 'НБКИ', value: 'nbki' },
  { label: 'Эквифакс', value: 'equ' }
]

export default defineComponent({
  name: 'LimitsSearchForm',

  components: {
    AbstractForm,
    Limit,
    Checkbox,
    Accordion,
    AccordionTab,
    Dialog,
    Button
  },

  props: {
    actions: {
      type: Function,
      default: null
    },
    onSendClosedLimits: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      formLoading: false,
      sendingInProgress: false,
      showSendDialog: false,
      limitsToSend: [],
      bureausToSend: [],

      searchForm: {
        ids: new FormModel('ID лимитов', {
          placeholder: 'Введите ID лимитов (через запятую, пробел или новую строку)',
          description: 'Например: 12345, 67890 111213\n141516'
        }),
        cbUuids: new FormModel('CB UUID', {
          type: 'text',
          placeholder: 'Введите UUID (через запятую, пробел или новую строку)',
          description: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx-x xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx-x\nxxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx-x'
        })
      },

      limits: [],
      bureaus: BUREAUS,
      selectedBureaus: {},
      globalSelectedBureaus: []
    }
  },

  computed: {
    hasClosedLimits() {
      return this.limits.some(limit => limit.status === 'closed')
    },

    closedLimits() {
      return this.limits.filter(limit => limit.status === 'closed')
    },

    sendDialogHeader() {
      if (this.limitsToSend.length === 1) {
        return 'Отправка закрытого лимита'
      }
      return `Отправка ${this.limitsToSend.length} закрытых лимитов`
    }
  },

  methods: {
    async searchLimits() {
      if (!this.validateSearchForm()) {
        return
      }

      this.formLoading = true
      this.limits = []
      this.globalSelectedBureaus = []

      try {
        const { ids, cbUuids } = this.prepareSearchParams()
        const requests = []

        if (ids.length > 0) {
          ids.forEach(id => {
            requests.push(this.fetchLimit({ id }))
          })
        }

        if (cbUuids.length > 0) {
          cbUuids.forEach(uuid => {
            requests.push(this.fetchLimit({ cbUuid: uuid }))
          })
        }

        const responses = await Promise.allSettled(requests)
        this.handleSearchResponses(responses)
      } catch (error) {
        ResponseService.handleErrorResponseWithForm(
            error,
            this.searchForm,
            this.$toast
        )
      } finally {
        this.formLoading = false
      }
    },

    async fetchLimit(params) {
      try {
        const response = await LimitService.getLimit(params.id, params.cbUuid)
        return { ...response, searchParams: params }
      } catch (error) {
        throw { error, searchParams: params }
      }
    },

    parseInputString(input) {
      if (!input) return []

      const items = String(input)
          .split(/[,\s\n]+/)
          .map(item => item.trim())
          .filter(item => item.length > 0)

      return [...new Set(items)]
    },

    prepareSearchParams() {
      const params = {
        ids: [],
        cbUuids: []
      }

      if (this.searchForm.ids.value) {
        params.ids = this.parseInputString(this.searchForm.ids.value)
            .filter(id => ID_REGEX.test(id))
      }

      if (this.searchForm.cbUuids.value) {
        params.cbUuids = this.parseInputString(this.searchForm.cbUuids.value)
            .filter(uuid => UUID_REGEX.test(uuid))
      }

      return params
    },

    validateSearchForm() {
      const idsValue = this.searchForm.ids.value
      const uuidsValue = this.searchForm.cbUuids.value

      if (!idsValue?.trim() && !uuidsValue?.trim()) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Ошибка валидации',
          detail: 'Заполните хотя бы одно поле для поиска',
          life: 5000
        })
        return false
      }

      if (idsValue?.trim()) {
        const ids = this.parseInputString(idsValue)
        const invalidIds = ids.filter(id => !ID_REGEX.test(id))

        if (invalidIds.length > 0) {
          this.searchForm.ids.state = 'error'
          this.searchForm.ids.feedback = `Неверный формат ID: ${invalidIds.join(', ')}`

          this.$toast.add({
            severity: 'warn',
            summary: 'Ошибка валидации',
            detail: 'Проверьте формат ID (только цифры)',
            life: 5000
          })
          return false
        }

        if (ids.length === 0) {
          this.searchForm.ids.state = 'error'
          this.searchForm.ids.feedback = 'Нет корректных ID для поиска'

          this.$toast.add({
            severity: 'warn',
            summary: 'Ошибка валидации',
            detail: 'Нет корректных ID для поиска',
            life: 5000
          })
          return false
        }
      }

      if (uuidsValue?.trim()) {
        const uuids = this.parseInputString(uuidsValue)
        const invalidUuids = uuids.filter(uuid => !UUID_REGEX.test(uuid))

        if (invalidUuids.length > 0) {
          this.searchForm.cbUuids.state = 'error'
          this.searchForm.cbUuids.feedback = `Неверный формат UUID: ${invalidUuids.join(', ')}`

          this.$toast.add({
            severity: 'warn',
            summary: 'Ошибка валидации',
            detail: 'Проверьте формат UUID',
            life: 5000
          })
          return false
        }

        if (uuids.length === 0) {
          this.searchForm.cbUuids.state = 'error'
          this.searchForm.cbUuids.feedback = 'Нет корректных UUID для поиска'

          this.$toast.add({
            severity: 'warn',
            summary: 'Ошибка валидации',
            detail: 'Нет корректных UUID для поиска',
            life: 5000
          })
          return false
        }
      }

      return true
    },

    handleSearchResponses(responses) {
      const foundLimits = []
      const errors = []

      responses.forEach((result) => {
        if (result.status === 'fulfilled' && result.value?.limit) {
          foundLimits.push(result.value.limit)
        } else if (result.status === 'rejected') {
          errors.push({
            params: result.reason.searchParams,
            error: result.reason.error
          })
        }
      })

      this.limits = foundLimits

      if (foundLimits.length > 0) {
        this.$toast.add({
          severity: 'success',
          summary: 'Успешно',
          detail: `Найдено лимитов: ${foundLimits.length}`,
          life: 3000
        })
      }

      if (errors.length > 0) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Не все лимиты найдены',
          detail: `Не удалось найти ${errors.length} лимитов`,
          life: 5000
        })
      }

      if (foundLimits.length === 0 && errors.length > 0) {
        this.$toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось найти ни одного лимита',
          life: 5000
        })
      }
    },

    getLimitHeader(limit, index) {
      const key = limit.id || limit.cbUuid
      const selectedBureausCount = this.selectedBureaus[key]?.length || 0

      if (limit.status === 'closed') {
        return `🔒 Лимит #${index + 1}: ${key} (закрыт) ${selectedBureausCount > 0 ? `✓ выбрано бюро: ${selectedBureausCount}` : ''}`
      } else {
        return `✅ Лимит #${index + 1}: ${key} (активен) ${selectedBureausCount > 0 ? `✓ выбрано бюро: ${selectedBureausCount}` : ''}`
      }
    },

    getActionItemsForLimit(limit) {
      const items = []

      if (limit.status === 'closed') {
        items.push({
          label: 'Отправить закрытый лимит',
          icon: 'pi pi-send',
          command: () => this.sendClosedLimit(limit),
          disabled: limit.status !== 'closed'
        })
      }

      if (this.actions) {
        const customActions = this.actions({
          limit,
          selectedBureaus: this.selectedBureaus[limit.id || limit.cbUuid] || []
        }) || []

        items.push(...customActions)
      }

      return items
    },

    hasSelectedBureausForLimit(limit) {
      const limitKey = limit.id || limit.cbUuid
      return this.selectedBureaus[limitKey]?.length > 0
    },

    getSelectedBureausLabels() {
      const bureauMap = {
        'nbki': 'НБКИ',
        'equ': 'Эквифакс'
      }

      return this.bureausToSend
          .map(b => bureauMap[b] || b)
          .join(', ')
    },

    sendClosedLimit(limit) {
      if (limit.status !== 'closed') {
        this.$toast.add({
          severity: 'warn',
          summary: 'Ошибка',
          detail: 'Можно отправлять только закрытые лимиты',
          life: 3000
        })
        return
      }

      const limitKey = limit.id || limit.cbUuid
      const selectedBureaus = this.selectedBureaus[limitKey] || []

      if (selectedBureaus.length === 0) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Ошибка',
          detail: 'Выберите хотя бы одно бюро для отправки',
          life: 3000
        })
        return
      }

      this.limitsToSend = [limit]
      this.bureausToSend = [...selectedBureaus]
      this.showSendDialog = true
    },

    sendClosedLimits() {
      if (this.globalSelectedBureaus.length === 0) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Ошибка',
          detail: 'Выберите хотя бы одно бюро для отправки',
          life: 3000
        })
        return
      }

      const closedLimits = this.closedLimits

      if (closedLimits.length === 0) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Ошибка',
          detail: 'Нет закрытых лимитов для отправки',
          life: 3000
        })
        return
      }

      this.limitsToSend = closedLimits
      this.bureausToSend = [...this.globalSelectedBureaus]
      this.showSendDialog = true
    },

    async confirmSendClosedLimits() {
      if (this.limitsToSend.length === 0 || this.bureausToSend.length === 0) {
        this.showSendDialog = false
        return
      }

      const closedLimitsToSend = this.limitsToSend.filter(limit => limit.status === 'closed')

      if (closedLimitsToSend.length === 0) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Ошибка',
          detail: 'Нет закрытых лимитов для отправки',
          life: 5000
        })
        this.showSendDialog = false
        return
      }

      this.sendingInProgress = true

      try {
        const uuids = closedLimitsToSend
            .map(limit => limit.cbUuid)
            .filter(uuid => uuid)

        if (uuids.length === 0) {
          throw new Error('Нет UUID для отправки')
        }

        if (this.onSendClosedLimits) {
          await this.onSendClosedLimits({
            uuids: uuids,
            bureaus: this.bureausToSend
          })
        } else {
          await this.sendClosedLimitsRequest(uuids, this.bureausToSend)
        }

        this.$toast.add({
          severity: 'success',
          summary: 'Успешно',
          detail: `Отправлено лимитов: ${closedLimitsToSend.length}`,
          life: 3000
        })

        this.globalSelectedBureaus = []

        this.showSendDialog = false
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: error.message || 'Не удалось отправить лимиты',
          life: 5000
        })
      } finally {
        this.sendingInProgress = false
      }
    },
    async sendClosedLimitsRequest(uuids, bureaus) {
      LimitService.closeLimit(uuids, bureaus).then((res) => {
        showToast(res.feedback, res.error);
      })
          .catch((err) => {
            showErrorToast(err);
          })
          .finally(() => {
            if (this.afterClose !== null) {
              this.afterClose();
            }
          })
    },
    handleAfterClear() {
      this.limits = []
      this.selectedBureaus = {}
      this.globalSelectedBureaus = []
    },

    setLoading(value) {
      this.loading = Boolean(value)
    },

    clear() {
      this.$refs.form?.clear()
    }
  }
})
</script>

<style scoped>
.confirmation-content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
</style>
